import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { BACKEND_URL } from "../lib/api";
import { useCart } from "../lib/cart";
import { useWishlist } from "../lib/wishlist";
import { waLink } from "../lib/whatsapp";
import { useSEO } from "../lib/seo";
import { MessageCircle, Heart, Ruler } from "lucide-react";
import BlurHashImage from "../components/BlurHashImage";
import ShareButton from "../components/ShareButton";

function imageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

function priceLabel(p) {
  if (p == null) return "Get Quote";
  return `₹${Number(p).toLocaleString("en-IN")}`;
}

function qualityName(q) {
  return typeof q === "string" ? q : String(q?.name || "");
}

function qualityPrice(q) {
  if (!q || typeof q === "string") return null;
  const value = Number(q.price);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const requestedReturnPath = searchParams.get("from");
  const backToShopPath = requestedReturnPath && requestedReturnPath.startsWith("/shop") && !requestedReturnPath.startsWith("//") ? requestedReturnPath : "/shop";
  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [sharedSizeChart, setSharedSizeChart] = useState(null);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const sizeChartAreaRef = useRef(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [customColor, setCustomColor] = useState("");
  const [qty, setQty] = useState(1);
  const [printing, setPrinting] = useState("");
  const [tshirtStyle, setTshirtStyle] = useState("");
  const [customText, setCustomText] = useState("");
  const [customName, setCustomName] = useState("");
  const [customNumber, setCustomNumber] = useState("");
  const [designId, setDesignId] = useState(searchParams.get("design") || "");
  const [notes, setNotes] = useState("");
  const [selectedImage, setSelectedImage] = useState("");
  const [quality, setQuality] = useState("");
  const [giftOptions, setGiftOptions] = useState({});

  // Gift customisations belong to one product only; do not carry values to another slug.
  useEffect(() => {
    setGiftOptions({});
  }, [slug]);
  const [designQualities, setDesignQualities] = useState([]);
  const [designDiscountPercent, setDesignDiscountPercent] = useState(0);
  const [customPricing, setCustomPricing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [displayPricing, setDisplayPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  useEffect(() => {
    if (!showSizeChart) return undefined;

    const closeOnOutsidePointer = (event) => {
      if (sizeChartAreaRef.current && !sizeChartAreaRef.current.contains(event.target)) {
        setShowSizeChart(false);
      }
    };
    const closeOnViewportMove = () => setShowSizeChart(false);

    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    window.addEventListener("scroll", closeOnViewportMove, true);
    window.addEventListener("touchmove", closeOnViewportMove, { passive: true });
    window.addEventListener("resize", closeOnViewportMove);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
      window.removeEventListener("scroll", closeOnViewportMove, true);
      window.removeEventListener("touchmove", closeOnViewportMove);
      window.removeEventListener("resize", closeOnViewportMove);
    };
  }, [showSizeChart]);

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/size-chart").then(({ data }) => { if (!cancelled) setSharedSizeChart(data?.size_chart || null); })
      .catch(() => { if (!cancelled) setSharedSizeChart(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Avoid briefly showing the previous product or design pricing while a new route loads.
    setProduct(null);
    setDisplayPricing(null);
    setPricingLoading(false);
    setDesignQualities([]);
    setDesignDiscountPercent(0);

    api.get("/custom-pricing")
      .then((r) => { if (!cancelled) setCustomPricing(r.data); })
      .catch(() => { if (!cancelled) setCustomPricing(null); });

    api.get(`/products/${slug}`).then((r) => {
      if (cancelled) return;
      setProduct(r.data);
      setSize((r.data.sizes || [])[0] || "");
      setColor((r.data.colors || [])[0] || "");
      setPrinting((r.data.printing_methods || [])[0] || "");
      setTshirtStyle((r.data.styles || [])[0] || "");
      const firstQuality = (r.data.qualities || [])[0];
      setQuality(qualityName(firstQuality));
      if (!searchParams.get("design")) {
        setDesignDiscountPercent(Number(r.data.discount_percent || 0));
      }
      setSelectedImage(imageUrl((r.data.images || [])[0] || r.data.image || ""));
    }).catch(() => {
      if (!cancelled) setProduct(null);
    });
    const selectedDesign = searchParams.get("design");

    const applyDesignQualities = (data) => {
      if (cancelled) return;
      const configured = Array.isArray(data?.qualities) ? data.qualities : [];
      setDesignQualities(configured);
      setDesignDiscountPercent(Number(data?.discount_percent || 0));
      if (configured.length > 0) {
        const firstQuality = configured[0];
        setQuality(qualityName(firstQuality));
      }
    };

    if (selectedDesign) {
      api.get(`/designs/${encodeURIComponent(selectedDesign)}`)
        .then((r) => applyDesignQualities(r.data))
        .catch(() => {
          if (cancelled) return;
          setDesignQualities([]);
          setDesignDiscountPercent(0);
        });
    } else {
      setDesignQualities([]);
      if (/^PMT-/i.test(slug || "")) {
        api.get(`/designs/${encodeURIComponent(slug)}`)
          .then((r) => applyDesignQualities(r.data))
          .catch(() => {});
      }
    }

    return () => { cancelled = true; };
  }, [slug, searchParams]);

  // Keep the main image in sync with a configured colour/style variant.
  // This hook stays above the loading return so hook order remains stable.
  useEffect(() => {
    if (!product) return;
    const variants = Array.isArray(product.variant_images) ? product.variant_images : [];
    const exact = variants.find((v) => (v.style || "") === (tshirtStyle || "") && (v.color || "") === (color || ""));
    const colorOnly = variants.find((v) => !(v.style || "") && (v.color || "") === (color || ""));
    const matchingImage = exact?.image || colorOnly?.image;
    // Do not keep a previous variant's image when the newly selected variant
    // has no dedicated image; fall back to the product's primary image.
    const fallbackImage = (Array.isArray(product.images) ? product.images[0] : "") || product.image || "";
    setSelectedImage(imageUrl(matchingImage || fallbackImage));
  }, [product, tshirtStyle, color]);

  useEffect(() => {
    if (!product) return;

    let cancelled = false;
    const calculateDisplayPrice = async () => {
      if (color === "Custom" && !customColor.trim()) {
        setDisplayPricing(null);
        return;
      }

      setPricingLoading(true);
      try {
        const res = await api.post("/product-price", {
          product_slug: product.slug,
          design_id: designId || null,
          product_name: product.name,
          product_type: product.product_type === "gift" ? "gift" : (product.slug === "custom-t-shirts" ? "T-Shirts" : product.category),
          size,
          color: color === "Custom" ? customColor.trim() : color,
          quality: quality || null,
          quantity: qty,
          tshirt_style: tshirtStyle,
          printing_method: printing,
          custom_text: customText || null,
          custom_name: customName || null,
          custom_number: customNumber || null,
          gift_options: product.product_type === "gift" && Object.keys(giftOptions).length ? giftOptions : null,
        });
        if (!cancelled) setDisplayPricing(res.data);
      } catch {
        if (!cancelled) setDisplayPricing(null);
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    };

    calculateDisplayPrice();
    return () => {
      cancelled = true;
    };
  }, [
    product,
    size,
    color,
    customColor,
    qty,
    printing,
    tshirtStyle,
    customText,
    customName,
    customNumber,
    designId,
    quality,
    giftOptions,
  ]);

  useSEO({
    title: product ? `${product.name} — PrintMyTees` : "Product — PrintMyTees",
    description: product ? `${product.name} · ${product.description}` : "Custom apparel product from PrintMyTees.",
  });

  if (!product) return <div className="container-pmt py-20" data-testid="product-loading">Loading…</div>;

  const styles = Array.isArray(product.styles) ? product.styles : [];
  const supportsJerseyNumber = product.slug === "sports-jerseys" || product.category === "Sports Jerseys";
  const productQualities = Array.isArray(product.qualities) ? product.qualities : [];
  const qualities = designQualities.length > 0 ? designQualities : productQualities;
  const selectedQualityOption = qualities.find((q) => qualityName(q) === quality);
  const selectedQualityPrice = qualityPrice(selectedQualityOption);
  const pricingGroup =
    customPricing?.pricing?.[
      product.slug === "custom-t-shirts" ? "T-Shirts" : product.category
    ];
  const defaultDashboardStyle =
    pricingGroup?.styles && Object.keys(pricingGroup.styles).length > 0
      ? Object.keys(pricingGroup.styles)[0]
      : "";
  const effectiveTshirtStyle = tshirtStyle || defaultDashboardStyle;
  const dashboardStylePrice =
    !designId && pricingGroup?.styles?.[effectiveTshirtStyle] != null
      ? Number(pricingGroup.styles[effectiveTshirtStyle])
      : null;
  const baseDisplayPrice =
    selectedQualityPrice ??
    dashboardStylePrice ??
    Number(product.price || 0);
  const discountPercent = designId
    ? designDiscountPercent
    : Number(product.discount_percent || 0);
  const discountedBasePrice = baseDisplayPrice > 0
    ? Math.round(baseDisplayPrice * (1 - Math.min(100, Math.max(0, discountPercent)) / 100))
    : 0;
  const printingCharge =
    product.product_type !== "gift" && !designId && pricingGroup?.printing?.[printing] != null
      ? Number(pricingGroup.printing[printing])
      : 0;
  const nameCharge =
    product.product_type !== "gift" && !designId && customName && customPricing?.personalisation?.name != null
      ? Number(customPricing.personalisation.name)
      : 0;
  const numberCharge =
    product.product_type !== "gift" && !designId && supportsJerseyNumber && customNumber && customPricing?.personalisation?.number != null
      ? Number(customPricing.personalisation.number)
      : 0;
  const textCharge =
    product.product_type !== "gift" && !designId && customText && customPricing?.personalisation?.custom_text != null
      ? Number(customPricing.personalisation.custom_text)
      : 0;
  const personalisationCharge = nameCharge + numberCharge + textCharge;
  const localDisplayedUnitTotal =
    discountedBasePrice + printingCharge + personalisationCharge;
  const displayedUnitTotal = displayPricing?.unit_price ?? localDisplayedUnitTotal;
  const displayedTotal = displayPricing?.total_price ?? (displayedUnitTotal * qty);
  const variantImages = Array.isArray(product.variant_images) ? product.variant_images : [];

  const variantImage = (style, selectedColor) => {
    const exact = variantImages.find((v) => (v.style || "") === (style || "") && (v.color || "") === (selectedColor || ""));
    if (exact?.image) return imageUrl(exact.image);
    const colorOnly = variantImages.find((v) => !(v.style || "") && (v.color || "") === (selectedColor || ""));
    if (colorOnly?.image) return imageUrl(colorOnly.image);
    return "";
  };

  const shopImage = selectedImage || variantImage(tshirtStyle, color) || imageUrl(product.image);

  const handleGalleryTouchStart = (event) => {
    if (productGallery.length < 2) return;
    const touch = event.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const handleGalleryTouchEnd = (event) => {
    if (productGallery.length < 2 || touchStartX.current == null || touchStartY.current == null) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;

    // Only treat predominantly horizontal gestures as gallery swipes.
    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    const currentIndex = Math.max(0, productGallery.indexOf(selectedImage));
    const nextIndex = deltaX < 0
      ? (currentIndex + 1) % productGallery.length
      : (currentIndex - 1 + productGallery.length) % productGallery.length;
    setSelectedImage(productGallery[nextIndex]);
  };

  const productGallery = Array.from(
    new Set(
      [
        variantImage(tshirtStyle, color),
        ...variantImages.map((variant) => variant?.image),
        ...(Array.isArray(product.images) && product.images.length > 0
          ? product.images
          : product.image
            ? [product.image]
            : []),
      ]
        .filter(Boolean)
        .map(imageUrl)
        .filter(Boolean)
    )
  ).slice(0, 5);

  const buildItem = (priced) => ({
    product_slug: product.slug,
    product_name: product.name,
    product_type: product.product_type === "gift" ? "gift" : (product.slug === "custom-t-shirts" ? "T-Shirts" : product.category),
    image: shopImage || null,
    blurhash: product.blurhash || null,
    size,
    color: color === "Custom" ? customColor.trim() : color,
    quantity: qty,
    tshirt_style: tshirtStyle,
    quality: quality || null,
    printing_method: printing,
    custom_text: customText || null,
    custom_name: customName || null,
    custom_number: customNumber || null,
    design_id: designId || null,
    notes: notes || null,
    gift_options: Object.keys(giftOptions).length ? giftOptions : null,
    gift_option_labels: Object.keys(giftOptions).length
      ? Object.fromEntries((product.gift_options || []).map((option, index) => [String(option.name || option.key || `option_${index}`), String(option.label || option.name || "Gift detail")]))
      : null,
    price: Number(priced.unit_price),
    total_price: Number(priced.total_price),
    original_price: Number(priced.original_unit_price ?? priced.unit_price),
    discount_percent: Number(priced.discount_percent || 0),
  });

  const getPrice = async () => {
    if (product.product_type === "gift") {
      for (const [index, option] of (product.gift_options || []).entries()) {
        if (!option.required) continue;
        const key = String(option.name || option.key || `option_${index}`);
        if (!String(giftOptions[key] || "").trim()) {
          throw new Error(`Please enter ${option.label || option.name || "the required gift detail"}.`);
        }
      }
    }
    if (color === "Custom" && !customColor.trim()) {
      throw new Error("Please enter your custom colour.");
    }

    const res = await api.post("/product-price", {
      product_slug: product.slug,
      design_id: designId || null,
      product_name: product.name,
      product_type: product.product_type === "gift" ? "gift" : (product.slug === "custom-t-shirts" ? "T-Shirts" : product.category),
      size,
      color: color === "Custom" ? customColor.trim() : color,
      quality: quality || null,
      quantity: qty,
      tshirt_style: effectiveTshirtStyle,
      printing_method: printing,
      custom_text: customText || null,
      custom_name: customName || null,
      custom_number: customNumber || null,
      gift_options: product.product_type === "gift" && Object.keys(giftOptions).length ? giftOptions : null,
    });
    return res.data;
  };

  const onAdd = async () => {
    if (adding) return;
    setAdding(true);
    try {
      const priced = await getPrice();
      addItem(buildItem(priced));
      toast.success("Added to cart");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || "Could not calculate the price.");
    } finally {
      setAdding(false);
    }
  };

  const onBuy = async () => {
    if (adding) return;
    setAdding(true);
    try {
      const priced = await getPrice();
      addItem(buildItem(priced));
      nav("/checkout");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || "Could not calculate the price.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="container-pmt py-12 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="product-page">
      <div className="lg:col-span-6">
        <div
          className="relative aspect-[4/5] bg-[#F3EDE2] border border-[#E6DFD5] touch-pan-y select-none"
          onTouchStart={handleGalleryTouchStart}
          onTouchEnd={handleGalleryTouchEnd}
          onTouchCancel={() => { touchStartX.current = null; touchStartY.current = null; }}
        >
          {product.slug === "custom-t-shirts" ? (
            <BlurHashImage
              src={shopImage}
              blurHash={product.blurhash}
              alt={`${color === "Custom" && customColor.trim() ? customColor.trim() : color} ${tshirtStyle} T-shirt`}
              className="w-full h-full object-contain"
            />
          ) : (
            shopImage && (
              <BlurHashImage
                src={shopImage}
                blurHash={product.blurhash}
                alt={product.name}
                className="w-full h-full object-contain"
              />
            )
          )}
          <div className="absolute right-3 top-3 z-20">
            </div>
        </div>
        <div className="mt-3 text-[10px] leading-relaxed text-stone-500">
          📌 Product images are for reference. Actual product colour, fabric, and print may vary slightly due to screen settings and production.
        </div>
        {productGallery.length > 0 && (
          <div className="mt-3 grid grid-cols-5 gap-2">
            {productGallery.length > 1 && (
              <div className="col-span-5 text-[10px] uppercase tracking-wider text-stone-500">
                {productGallery.length} product views
              </div>
            )}
            {productGallery.map((im, i) => (
              <button
                type="button"
                key={im}
                onClick={() => setSelectedImage(im)}
                className={`border focus:outline-none focus:ring-2 focus:ring-[#D95328]/30 ${selectedImage === im ? "border-[#141413] ring-1 ring-[#141413]" : "border-[#E6DFD5]"}`}
                aria-label={`View product image ${i + 1}`}
              >
                <BlurHashImage
                  src={im}
                  blurHash={i === 0 ? product.blurhash : ""}
                  alt={product.name + " view " + (i + 1)}
                  className="aspect-square w-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="lg:col-span-6">
        <div className="flex w-full min-w-0 items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="overline">{product.category}</div>
            <h1 className="display-h2 mt-2 break-words" data-testid="product-name">{product.name}</h1>
          </div>
          <div className="ml-auto flex shrink-0 flex-nowrap items-center justify-end gap-2" data-testid="product-detail-actions">
          <button
            type="button"
            onClick={() => toggleWishlist({
              type: "product",
              id: product.slug,
              name: product.name,
              image: shopImage,
              price: product.price,
              category: product.category,
            })}
            className="w-11 h-11 shrink-0 rounded-full border border-[#E6DFD5] bg-white flex items-center justify-center hover:text-[#D95328]"
            aria-label={isWishlisted("product", product.slug) ? "Remove from wishlist" : "Add to wishlist"}
            data-testid="product-detail-wishlist"
          >
            <Heart size={20} fill={isWishlisted("product", product.slug) ? "currentColor" : "none"} className={isWishlisted("product", product.slug) ? "text-[#D95328]" : ""} />
          </button>
          <ShareButton title={product.name} url={`/shop/${product.slug}`} />
          </div>
        </div>

        {product.price == null && (
          <p className="mt-2 text-xs text-stone-500" data-testid="product-price-message">
            Price will be confirmed after customisation.
          </p>
        )}
        <p className="mt-4 text-stone-700">{product.long_description || product.description}</p>

        <div className="mt-8 space-y-5">
          {styles.length > 0 && (
            <div>
              <div className="label-pmt">Style / Variant</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {styles.map((style) => (
                  <button key={style} type="button" onClick={() => setTshirtStyle(style)} className={`px-3 py-2 text-xs font-semibold uppercase border ${tshirtStyle === style ? "bg-[#141413] text-[#FAF7F2] border-[#141413]" : "bg-white border-[#E6DFD5] hover:border-[#141413]"}`}>
                    {style}
                  </button>
                ))}
              </div>
            </div>
          )}

          {qualities.length > 0 && (
            <div>
              <div className="label-pmt">{product.product_type === "gift" ? "Product Option" : "T-Shirt Quality / GSM"}</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {qualities.map((q) => {
                  const name = qualityName(q);
                  const price = qualityPrice(q);
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setQuality(name)}
                      className={`px-3 py-1.5 text-xs font-semibold uppercase border ${
                        quality === name
                          ? "bg-[#141413] text-[#FAF7F2] border-[#141413]"
                          : "bg-white border-[#E6DFD5] hover:border-[#141413]"
                      }`}
                    >
                      {name}{price != null ? ` — ₹${Math.round(price * (1 - Math.min(100, Math.max(0, discountPercent)) / 100)).toLocaleString("en-IN")}` : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {product.product_type !== "gift" && <>
          <div>
            <div className="label-pmt">
              Size <span className="text-[#D95328]">*</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(product.sizes || []).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSize(s)}
                  data-testid={`product-size-${s}`}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase border ${
                    size === s
                      ? "bg-[#141413] text-[#FAF7F2] border-[#141413]"
                      : "bg-white border-[#E6DFD5] hover:border-[#141413]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4" ref={sizeChartAreaRef}>
            <button type="button" onClick={() => setShowSizeChart((open) => !open)} aria-expanded={showSizeChart} aria-controls="shared-shop-size-chart" className="inline-flex items-center gap-2 text-sm font-semibold text-[#D95328] hover:underline"><Ruler size={16}/>{showSizeChart ? "Hide Size Chart" : "View Size Chart"}</button>
            {showSizeChart && <div id="shared-shop-size-chart" className="mt-3 max-w-xl border border-[#E6DFD5] bg-white p-2 sm:p-3" data-testid="product-size-chart">
              <p className="mb-2 text-xs text-stone-500">Check chest, length and shoulder measurements before choosing your size.</p>
              {Array.isArray(sharedSizeChart?.headers) && sharedSizeChart.headers.length > 0 && Array.isArray(sharedSizeChart?.rows) && sharedSizeChart.rows.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead className="bg-[#F8F5F0]"><tr>{(sharedSizeChart.headers.length >= 4 && !sharedSizeChart.headers.some((h) => /chest|length|shoulder/i.test(String(h))) ? ["Size", "Chest", "Length", "Shoulder", ...sharedSizeChart.headers.slice(4)] : sharedSizeChart.headers).map((header, index) => <th key={`${header}-${index}`} className="px-2 py-1.5 text-left font-semibold border-b border-[#E6DFD5] whitespace-nowrap">{header || ["Size", "Chest", "Length", "Shoulder"][index] || `Measurement ${index + 1}`}</th>)}</tr></thead>
                      <tbody>{sharedSizeChart.rows.map((row, rowIndex) => <tr key={rowIndex} className="border-b border-[#E6DFD5]">{(sharedSizeChart.headers.length >= 4 && !sharedSizeChart.headers.some((h) => /chest|length|shoulder/i.test(String(h))) ? ["Size", "Chest", "Length", "Shoulder", ...sharedSizeChart.headers.slice(4)] : sharedSizeChart.headers).map((_, cellIndex) => <td key={cellIndex} className="px-2 py-1.5 whitespace-nowrap">{row[cellIndex] ?? "—"}</td>)}</tr>)}</tbody>
                    </table>
                  </div>
                  <div className="mt-3 border-t border-[#E6DFD5] pt-2 text-xs leading-relaxed text-stone-600">
                    <h3 className="mb-1 font-semibold text-stone-900">Measurement Guidelines</h3>
                    <p><strong>Chest:</strong> Measure straight across the garment under the armholes.</p>
                    <p><strong>Length:</strong> Measure from the highest shoulder point beside the neck to the bottom hem.</p>
                    <p><strong>Shoulder:</strong> Measure across the back from one shoulder seam to the other.</p>
                    <p className="mt-1">Lay the garment flat without stretching it and compare in the same units shown in the chart.</p>
                  </div>
                </>
              ) : (
                <p className="text-xs text-stone-500">Size measurements have not been added yet.</p>
              )}
            </div>}
          </div>

          <div>
            <div className="label-pmt">
              Colour <span className="text-[#D95328]">*</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {[...(product.colors || []), "Custom"].filter(
                (c, i, arr) => arr.indexOf(c) === i
              ).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setColor(c);
                    if (c !== "Other") setCustomColor("");
                  }}
                  data-testid={`product-color-${c}`}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase border ${
                    color === c
                      ? "bg-[#141413] text-[#FAF7F2] border-[#141413]"
                      : "bg-white border-[#E6DFD5] hover:border-[#141413]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            {color === "Custom" && (
              <input
                className="input-pmt mt-2"
                type="text"
                placeholder="Enter your colour"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                required
                aria-label="Custom colour"
              />
            )}
          </div>

          <div>
            <div className="label-pmt">
              Printing Method <span className="text-[#D95328]">*</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(product.printing_methods || []).map((method) => (
                <button
                  key={method}
                  type="button"
                  onClick={() => setPrinting(method)}
                  data-testid={`product-printing-${method}`}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase border ${
                    printing === method
                      ? "bg-[#141413] text-[#FAF7F2] border-[#141413]"
                      : "bg-white border-[#E6DFD5] hover:border-[#141413]"
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>
          </>}

          {!product.product_type || product.product_type !== "gift" ? <>
          <div className="pt-2">
            <div className="border border-[#E6DFD5] bg-[#F8F5F0] p-4">
              <div className="font-bold uppercase text-sm tracking-wide">Personalise Your Order</div>
              <p className="mt-1 text-xs text-stone-500">Add your design or printing details. All fields are optional.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="label-pmt">Quantity</div>
              <div className="flex items-center border border-[#E6DFD5] bg-white h-11">
                <button
                  type="button"
                  onClick={() => setQty((current) => Math.max(1, current - 1))}
                  disabled={qty <= 1}
                  className="w-11 h-full text-lg font-semibold hover:bg-[#F3EDE2] disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <div className="flex-1 text-center font-semibold" data-testid="product-qty">{qty}</div>
                <button
                  type="button"
                  onClick={() => setQty((current) => Math.min(1000, current + 1))}
                  disabled={qty >= 1000}
                  className="w-11 h-full text-lg font-semibold hover:bg-[#F3EDE2] disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <p className="mt-1 text-[11px] text-stone-500">Maximum 1000 items per order.</p>
            </div>
            <div>
              <div className="label-pmt">Design ID (optional)</div>
              <input value={designId} onChange={(e) => setDesignId(e.target.value)} className="input-pmt" placeholder="e.g. PMT-014" data-testid="product-design-id" />
              <Link to="/designs" className="text-xs underline text-stone-500 mt-1 inline-block">Browse designs</Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="label-pmt">Custom Name (optional)</div>
              <input value={customName} onChange={(e) => setCustomName(e.target.value)} className="input-pmt" placeholder="e.g. RAJ" data-testid="product-custom-name" />
            </div>
            {supportsJerseyNumber && (
              <div>
                <div className="label-pmt">Jersey Number (optional)</div>
                <input value={customNumber} onChange={(e) => setCustomNumber(e.target.value)} className="input-pmt" placeholder="e.g. 07" data-testid="product-custom-number" inputMode="numeric" />
              </div>
            )}
          </div>

          <div>
            <div className="label-pmt">Custom Text (optional)</div>
            <input value={customText} onChange={(e) => setCustomText(e.target.value)} className="input-pmt" placeholder="Line to print" data-testid="product-custom-text" />
          </div>

          </> : null}

          {product.product_type === "gift" && (
            <div className="w-full max-w-[220px]">
              <div className="label-pmt">Quantity</div>
              <div className="flex items-center border border-[#E6DFD5] bg-white h-9">
                <button type="button" onClick={() => setQty((current) => Math.max(1, current - 1))} disabled={qty <= 1} className="w-9 h-full text-base font-semibold hover:bg-[#F3EDE2] disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Decrease quantity">−</button>
                <div className="flex-1 text-center text-sm font-semibold" data-testid="product-qty">{qty}</div>
                <button type="button" onClick={() => setQty((current) => Math.min(1000, current + 1))} disabled={qty >= 1000} className="w-9 h-full text-base font-semibold hover:bg-[#F3EDE2] disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Increase quantity">+</button>
              </div>
              <p className="mt-1 text-[11px] text-stone-500">Maximum 1000 items per order.</p>
            </div>
          )}

          {product.product_type === "gift" && Array.isArray(product.gift_options) && product.gift_options.length > 0 && (
            <div className="space-y-4 border border-[#E6DFD5] bg-[#FAF7F2] p-4" data-testid="gift-personalisation-fields">
              <div>
                <div className="font-bold uppercase text-sm tracking-wide">Gift Personalisation</div>
                <p className="mt-1 text-xs text-stone-500">Add the details you want us to use for this gift.</p>
              </div>
              {product.gift_options.map((option, index) => {
                const key = String(option.name || option.key || `option_${index}`);
                const label = String(option.label || option.name || `Option ${index + 1}`);
                const type = String(option.type || "text").toLowerCase();
                const value = giftOptions[key] || "";
                const inputProps = {
                  value,
                  onChange: (event) => setGiftOptions((current) => ({ ...current, [key]: event.target.value })),
                  className: "input-pmt",
                  required: Boolean(option.required),
                  "data-testid": `gift-option-${key}`,
                };
                return (
                  <div key={key}>
                    <label className="label-pmt">{label}{option.required ? " *" : " (optional)"}</label>
                    {type === "select" && Array.isArray(option.choices) ? (
                      <select {...inputProps}>
                        <option value="">Choose {label.toLowerCase()}</option>
                        {option.choices.map((choice) => <option key={choice} value={choice}>{choice}</option>)}
                      </select>
                    ) : type === "textarea" ? (
                      <textarea {...inputProps} rows={3} maxLength={500} />
                    ) : (
                      <input {...inputProps} type="text" maxLength={200} placeholder={option.placeholder || label} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div>
            <div className="label-pmt">Special Instructions</div>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="input-pmt" data-testid="product-notes" />
          </div>

          <div className="p-4 bg-[#F3EDE2] border border-[#E6DFD5] text-xs text-stone-700">
            <div className="font-semibold text-stone-800">Have your own artwork or logo?</div>
            <a className="underline font-semibold inline-block mt-1" href={waLink(`Hi PrintMyTees, sending artwork for ${product.name}.`)} target="_blank" rel="noreferrer">Send it to us on WhatsApp →</a>
          </div>

          <div className="mt-3" data-testid="product-price-note">
            {discountPercent > 0 && baseDisplayPrice > 0 ? (
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="text-stone-500 line-through text-base">{priceLabel(baseDisplayPrice)}</span>
                <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-1">{discountPercent}% OFF</span>
              </div>
            ) : null}
            {pricingLoading && !displayPricing ? (
              <div className="border border-[#E6DFD5] bg-[#F8F5F0] p-3 text-sm text-stone-500">
                Calculating price…
              </div>
            ) : displayPricing ? (
              <div className="border border-[#E6DFD5] bg-[#F8F5F0] p-3 text-sm space-y-1">
                <div className="overline mb-2">Price Summary</div>
                {product.product_type !== "gift" && (
                  <div className="flex justify-between">
                    <span>Base price</span>
                    <span>{priceLabel(displayPricing.base_price)}</span>
                  </div>
                )}
                {displayPricing.printing_price > 0 && (
                  <div className="flex justify-between">
                    <span>{printing} printing</span>
                    <span>+ {priceLabel(displayPricing.printing_price)}</span>
                  </div>
                )}
                {displayPricing.personalisation_price > 0 && (
                  <div className="flex justify-between">
                    <span>Customisation</span>
                    <span>+ {priceLabel(displayPricing.personalisation_price)}</span>
                  </div>
                )}
                <div className="pt-2 mt-2 border-t border-[#E6DFD5] flex justify-between font-bold">
                  <span>{product.product_type === "gift" ? (qty === 1 ? "Price" : "Price / item") : "Price / item"}</span>
                  <span className="text-[#D95328] font-mono text-xl">{priceLabel(displayPricing.unit_price)}</span>
                </div>
                {(product.product_type !== "gift" || qty > 1) && (
                  <div className="pt-2 mt-1 flex justify-between text-sm font-semibold">
                    <span>Total for {qty} {qty === 1 ? "item" : "items"}</span>
                    <span>{priceLabel(displayPricing.total_price)}</span>
                  </div>
                )}
                <p className="pt-2 mt-1 border-t border-[#E6DFD5] text-[11px] text-stone-500">
                  Delivery charges are calculated separately at checkout.
                </p>
              </div>
            ) : (
              <>
                <div className="text-[#D95328] font-mono text-2xl font-bold">
                  Price unavailable
                </div>
                <div className="mt-2 text-sm font-semibold text-stone-700">
                  We’ll calculate your price when you request a quote.
                </div>
                <p className="mt-1 text-[11px] text-stone-500">
                  Delivery charges are calculated separately at checkout.
                </p>
              </>
            )}
          </div>

          <div className="flex flex-col gap-3 pt-2">
            {product.purchasable ? (
              <>
                <button
                  onClick={onAdd}
                  disabled={adding}
                  className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="product-add-cart"
                >
                  {adding ? "Adding..." : "Add to Cart"}
                </button>

                <button
                  onClick={onBuy}
                  disabled={adding}
                  className="btn-accent w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="product-buy-now"
                >
                  {adding ? "Processing..." : "Buy Now"}
                </button>

                <button
                  type="button"
                  onClick={() => nav("/cart")}
                  className="text-xs font-semibold underline underline-offset-4 text-stone-500 hover:text-[#141413] self-center py-1"
                  data-testid="product-go-cart"
                >
                  Go to Cart
                </button>
              </>
            ) : (
              <a
                href={waLink(
                  `Hi PrintMyTees, I want a custom quote for ${product.name} (T-Shirt Style: ${effectiveTshirtStyle}, Size: ${size}, Colour: ${color === "Custom" ? customColor.trim() : color}, Printing: ${printing}, Qty: ${qty}${quality ? ", Quality: " + quality : ""}${designId ? ", Design: " + designId : ""}${customName ? ", Name: " + customName : ""}${supportsJerseyNumber && customNumber ? ", Number: " + customNumber : ""}${customText ? ", Text: " + customText : ""}).`
                )}
                target="_blank"
                rel="noreferrer"
                className="btn-whatsapp"
                data-testid="product-wa-quote"
              >
                <MessageCircle size={14} /> Get Custom Quote
              </a>
            )}
          </div>
        </div>

        <section className="mt-5 rounded-xl border border-[#E6DFD5] bg-white p-4 sm:p-5" aria-label="Ordering information" data-testid="product-ordering-info">
          <h2 className="text-sm font-bold text-[#10264a]">Before you order</h2>
          <ul className="mt-3 space-y-2 text-xs leading-5 text-stone-600">
            <li className="flex gap-2"><span aria-hidden="true" className="font-bold text-[#D95328]">✓</span><span>Review your selected size, colour and personalisation before adding to cart.</span></li>
            <li className="flex gap-2"><span aria-hidden="true" className="font-bold text-[#D95328]">✓</span><span>Delivery charges are calculated at checkout and shown with your order total.</span></li>
          </ul>
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 border-t border-[#eee7dd] pt-3 text-xs font-semibold">
            <Link to="/shipping-policy" className="underline underline-offset-4 hover:text-[#D95328]">Shipping policy</Link>
            <Link to="/return-refund-policy" className="underline underline-offset-4 hover:text-[#D95328]">Returns & refunds</Link>
            <a href={waLink("Hi PrintMyTees, I have a question about " + product.name + ".")} target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-[#D95328]">Ask us on WhatsApp</a>
          </div>
        </section>

        <div className="mt-8 text-xs text-stone-500">
          <Link to={backToShopPath} className="underline">← Back to Shop</Link>
        </div>
      </div>
    </div>
  );
}
